import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Plus, 
  Target, 
  Shield, 
  TrendingDown, 
  GitBranch,
  Trash2,
  X,
  Loader2,
  AlertTriangle
} from 'lucide-react'
import { advancedOrdersApi, marketsApi } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import { cn } from '../lib/utils'
import { AdvancedOrderModal } from '../components/AdvancedOrderModal'

interface AdvancedOrder {
  id: string
  type: 'STOP_LOSS' | 'TAKE_PROFIT' | 'TRAILING_STOP' | 'BRACKET' | 'OCO'
  status: string
  side: 'YES' | 'NO'
  size: number
  market: {
    id: string
    question: string
    yesPrice: number
    noPrice: number
  }
  entryPrice?: number
  stopLossPrice?: number
  takeProfitPrice?: number
  trailingDistance?: number
  trailingPercent?: boolean
  finalPnl?: number
  createdAt: string
}

const orderTypeConfig = {
  STOP_LOSS: { icon: Shield, color: 'text-red-600', bg: 'bg-red-50', label: 'Stop Loss' },
  TAKE_PROFIT: { icon: Target, color: 'text-green-600', bg: 'bg-green-50', label: 'Take Profit' },
  TRAILING_STOP: { icon: TrendingDown, color: 'text-orange-600', bg: 'bg-orange-50', label: 'Trailing Stop' },
  BRACKET: { icon: GitBranch, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Bracket' },
  OCO: { icon: GitBranch, color: 'text-purple-600', bg: 'bg-purple-50', label: 'OCO' },
}

const statusConfig = {
  PENDING: { label: 'Pending', color: 'text-yellow-600', bg: 'bg-yellow-50' },
  ENTRY_FILLED: { label: 'Monitoring', color: 'text-blue-600', bg: 'bg-blue-50' },
  STOP_TRIGGERED: { label: 'Stop Hit', color: 'text-red-600', bg: 'bg-red-50' },
  PROFIT_TRIGGERED: { label: 'Profit Taken', color: 'text-green-600', bg: 'bg-green-50' },
  CANCELLED: { label: 'Cancelled', color: 'text-gray-600', bg: 'bg-gray-50' },
  EXPIRED: { label: 'Expired', color: 'text-gray-600', bg: 'bg-gray-50' },
}

export default function AdvancedOrders() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('active')

  const { data, isLoading } = useQuery({
    queryKey: ['advancedOrders', filter],
    queryFn: () => advancedOrdersApi.getAll({ 
      status: filter === 'active' ? 'PENDING,ENTRY_FILLED' : undefined 
    }).then(r => r.data),
  })

  const { data: summary } = useQuery({
    queryKey: ['advancedOrdersSummary'],
    queryFn: () => advancedOrdersApi.getSummary().then(r => r.data),
  })

  const cancelMutation = useMutation({
    mutationFn: (id: string) => advancedOrdersApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advancedOrders'] })
      queryClient.invalidateQueries({ queryKey: ['advancedOrdersSummary'] })
    },
  })

  const orders: AdvancedOrder[] = data?.orders || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Advanced Orders</h1>
          <p className="text-gray-600">
            {summary?.totalActive > 0 ? (
              <span className="text-blue-600 font-medium">{summary.totalActive} active orders</span>
            ) : (
              'Set up stop-losses, take-profits, and trailing stops'
            )}
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Order
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
        {(['active', 'all', 'completed'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-md transition-colors capitalize',
              filter === f
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Orders Grid */}
      {orders.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No advanced orders</h3>
          <p className="text-gray-500 max-w-md mx-auto mb-6">
            Create stop-loss, take-profit, trailing stop, or bracket orders to 
            automate your trading strategy and manage risk.
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create Your First Order
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {orders.map((order) => {
            const typeConfig = orderTypeConfig[order.type]
            const orderStatusConfig = statusConfig[order.status]
            const TypeIcon = typeConfig.icon

            return (
              <div
                key={order.id}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={cn('p-3 rounded-lg', typeConfig.bg)}>
                      <TypeIcon className={cn('w-6 h-6', typeConfig.color)} />
                    </div>

                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-semibold text-gray-900">{typeConfig.label}</span>
                        <span className={cn(
                          'px-2 py-0.5 rounded text-xs font-medium',
                          orderStatusConfig.bg,
                          orderStatusConfig.color
                        )}>
                          {orderStatusConfig.label}
                        </span>
                      </div>

                      <p className="text-sm text-gray-600 mt-1">{order.market.question}</p>

                      <div className="flex items-center space-x-4 mt-3 text-sm">
                        <span className={cn(
                          'font-medium',
                          order.side === 'YES' ? 'text-green-600' : 'text-red-600'
                        )}>
                          {order.side}
                        </span>
                        <span className="text-gray-500">{formatCurrency(order.size)} shares</span>
                        
                        {order.entryPrice && (
                          <span className="text-gray-500">
                            Entry: {(order.entryPrice * 100).toFixed(1)}¢
                          </span>
                        )}
                      </div>

                      {/* Order Details */}
                      <div className="flex items-center space-x-4 mt-2 text-sm">
                        {order.stopLossPrice && (
                          <span className="text-red-600">
                            Stop: {(order.stopLossPrice * 100).toFixed(1)}¢
                          </span>
                        )}
                        {order.takeProfitPrice && (
                          <span className="text-green-600">
                            Target: {(order.takeProfitPrice * 100).toFixed(1)}¢
                          </span>
                        )}
                        {order.trailingDistance && (
                          <span className="text-orange-600">
                            Trail: {order.trailingDistance}
                            {order.trailingPercent ? '%' : '¢'}
                          </span>
                        )}
                      </div>

                      {/* Results */}
                      {order.finalPnl !== undefined && order.finalPnl !== null && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <span className={cn(
                            'text-sm font-medium',
                            order.finalPnl >= 0 ? 'text-green-600' : 'text-red-600'
                          )}>
                            Final P&L: {order.finalPnl >= 0 ? '+' : ''}{formatCurrency(order.finalPnl)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    {(order.status === 'PENDING' || order.status === 'ENTRY_FILLED') && (
                      <button
                        onClick={() => cancelMutation.mutate(order.id)}
                        disabled={cancelMutation.isPending}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Cancel order"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Create Order Modal */}
      {showModal && (
        <AdvancedOrderModal onClose={() => setShowModal(false)} />
      )}
    </div>
  )
}
